import { useState } from 'react'
import { GetUserProfileOutput, updateUserDemographics } from '../sdk/user'
import { Locale } from './Locale'
import { GetRegionsOutput } from '../sdk/stats'
import './Demographic.css'

export type DemographicField = keyof GetUserProfileOutput

type Option = {
  value: string
  en: string
  zh_cn: string
  zh_hk: string
}

// Predefined options for each demographic field
const other_option: Option = {
  value: 'other',
  en: 'Other',
  zh_cn: '其他',
  zh_hk: '其他',
}
const occupation_options: Option[] = [
  { value: 'student', en: 'Student', zh_cn: '学生', zh_hk: '學生' },
  { value: 'designer', en: 'Designer', zh_cn: '设计师', zh_hk: '設計師' },
  { value: 'developer', en: 'Developer', zh_cn: '开发者', zh_hk: '開發者' },
  {
    value: 'marketing',
    en: 'Marketing Professional',
    zh_cn: '营销专业人员',
    zh_hk: '營銷專業人員',
  },
  {
    value: 'content-creator',
    en: 'Content Creator',
    zh_cn: '内容创作者',
    zh_hk: '內容創作者',
  },
  {
    value: 'educator',
    en: 'Educator',
    zh_cn: '教育工作者',
    zh_hk: '教育工作者',
  },
  {
    value: 'business-owner',
    en: 'Business Owner',
    zh_cn: '企业主',
    zh_hk: '企業主',
  },
]
const industry_options: Option[] = [
  { value: 'technology', en: 'Technology', zh_cn: '科技', zh_hk: '科技' },
  { value: 'education', en: 'Education', zh_cn: '教育', zh_hk: '教育' },
  {
    value: 'entertainment',
    en: 'Entertainment/Media',
    zh_cn: '娱乐/媒体',
    zh_hk: '娛樂/媒體',
  },
  {
    value: 'marketing',
    en: 'Marketing/Advertising',
    zh_cn: '营销/广告',
    zh_hk: '營銷/廣告',
  },
  {
    value: 'architecture',
    en: 'Architecture/Real Estate',
    zh_cn: '建筑/房地产',
    zh_hk: '建築/房地產',
  },
  {
    value: 'healthcare',
    en: 'Healthcare',
    zh_cn: '医疗保健',
    zh_hk: '醫療保健',
  },
  { value: 'finance', en: 'Finance', zh_cn: '金融', zh_hk: '金融' },
  {
    value: 'ecommerce',
    en: 'E-commerce',
    zh_cn: '电子商务',
    zh_hk: '電子商務',
  },
  { value: 'gaming', en: 'Gaming', zh_cn: '游戏', zh_hk: '遊戲' },
]
const primary_use_case_options: Option[] = [
  {
    value: 'personal',
    en: 'Personal projects',
    zh_cn: '个人项目',
    zh_hk: '個人項目',
  },
  {
    value: 'work',
    en: 'Work/Professional',
    zh_cn: '工作/专业',
    zh_hk: '工作/專業',
  },
  {
    value: 'education',
    en: 'Education/Learning',
    zh_cn: '教育/学习',
    zh_hk: '教育/學習',
  },
  { value: 'client', en: 'Client work', zh_cn: '客户工作', zh_hk: '客戶工作' },
  {
    value: 'portfolio',
    en: 'Portfolio building',
    zh_cn: '作品集建设',
    zh_hk: '作品集建設',
  },
  {
    value: 'social',
    en: 'Social media content',
    zh_cn: '社交媒体内容',
    zh_hk: '社交媒體內容',
  },
]
const discovery_source_options: Option[] = [
  {
    value: 'social-media',
    en: 'Social media',
    zh_cn: '社交媒体',
    zh_hk: '社交媒體',
  },
  {
    value: 'search',
    en: 'Search engine',
    zh_cn: '搜索引擎',
    zh_hk: '搜索引擎',
  },
  {
    value: 'referral',
    en: 'Friend referral',
    zh_cn: '朋友推荐',
    zh_hk: '朋友推薦',
  },
  { value: 'advertisement', en: 'Advertisement', zh_cn: '广告', zh_hk: '廣告' },
  {
    value: 'conference',
    en: 'Conference/Event',
    zh_cn: '会议/活动',
    zh_hk: '會議/活動',
  },
]

const experience_level_options: Option[] = [
  {
    value: 'no-experience',
    en: 'No experience in 3D/VR production',
    zh_cn: '无3D/VR制作经验',
    zh_hk: '無3D/VR製作經驗',
  },
  {
    value: 'learning',
    en: 'Learning 3D/VR',
    zh_cn: '正在学习3D/VR',
    zh_hk: '正在學習3D/VR',
  },
  {
    value: 'hobbyist',
    en: 'Hobbyist',
    zh_cn: '爱好者',
    zh_hk: '愛好者',
  },
  {
    value: 'professional',
    en: 'Professional',
    zh_cn: '专业',
    zh_hk: '專業',
  },
  {
    value: 'educator',
    en: '3D/VR Educator/Instructor',
    zh_cn: '3D/VR教育工作者/讲师',
    zh_hk: '3D/VR教育工作者/講師',
  },
  {
    value: 'expert',
    en: '3D/VR Expert/Researcher',
    zh_cn: '3D/VR专家/研究员',
    zh_hk: '3D/VR專家/研究員',
  },
]
const preferred_content_types_options: Option[] = [
  {
    value: 'architectural',
    en: 'Architectural scenes',
    zh_cn: '建筑场景',
    zh_hk: '建築場景',
  },
  {
    value: 'nature',
    en: 'Nature/Outdoor',
    zh_cn: '自然/户外',
    zh_hk: '自然/戶外',
  },
  {
    value: 'fantasy',
    en: 'Fantasy/Sci-fi',
    zh_cn: '奇幻/科幻',
    zh_hk: '奇幻/科幻',
  },
  {
    value: 'realistic',
    en: 'Realistic environments',
    zh_cn: '现实环境',
    zh_hk: '現實環境',
  },
  {
    value: 'abstract',
    en: 'Abstract/Artistic',
    zh_cn: '抽象/艺术',
    zh_hk: '抽象/藝術',
  },
  {
    value: 'character',
    en: 'Character scenes',
    zh_cn: '角色场景',
    zh_hk: '角色場景',
  },
]

// Helper functions to check if a value is predefined or custom
function isCustomValue(
  options: Option[],
  value: string | null | undefined,
): boolean {
  return !!value && !options.some(option => option.value === value)
}

function splitCustomValues(
  options: Option[],
  values: string[] | null | undefined,
) {
  const predefined_values: string[] = []
  const other_values: string[] = []
  let has_other = false
  values?.forEach(value => {
    if (!value) return
    if (value === 'other') {
      has_other = true
      return
    }
    if (isCustomValue(options, value)) {
      other_values.push(value)
      has_other = true
    } else {
      predefined_values.push(value)
    }
  })
  return { predefined_values, other_values, has_other }
}

function otherValue(value: string | null): string {
  if (!value) return ''
  if (value === 'other') return ''
  return value
}

export function Demographic(props: {
  regions: GetRegionsOutput['regions'] | undefined
  profile: GetUserProfileOutput | null
  setProfile: (
    updateFunction: (
      profile: GetUserProfileOutput | null,
    ) => GetUserProfileOutput | null,
  ) => void
}) {
  const { regions, profile, setProfile } = props

  const [saveDemographicError, setSaveDemographicError] = useState<
    string | null
  >(null)

  const other_occupation = isCustomValue(
    occupation_options,
    profile?.occupation,
  )
  const other_industry = isCustomValue(industry_options, profile?.industry)
  const other_discovery_source = isCustomValue(
    discovery_source_options,
    profile?.discovery_source,
  )
  const purpose = splitCustomValues(
    primary_use_case_options,
    profile?.primary_use_case,
  )
  const preferred_content_types = splitCustomValues(
    preferred_content_types_options,
    profile?.preferred_content_types,
  )

  function updateDemographic(
    field: DemographicField,
    value: string | string[],
  ) {
    if (!profile) return

    // Snapshot the old value for rollback when failed to save
    const oldValue = profile[field as keyof typeof profile]

    // Update local profile state immediately for responsive UI
    setProfile(prev => (prev ? { ...prev, [field]: value } : null))

    updateUserDemographics({
      params: { id: profile.user_id },
      body: { [field]: value },
    })
      .then(() => setSaveDemographicError(null))
      .catch(error => {
        setSaveDemographicError(String(error))
        // Revert the local state on error
        setProfile(prev => (prev ? { ...prev, [field]: oldValue } : null))
      })
  }

  function updateDemographicArray(
    field: DemographicField,
    value: string,
    checked: boolean,
  ) {
    if (!profile) return
    let array = profile[field] as string[] | null
    let newArray = array ? array.slice() : []
    if (checked) {
      newArray.push(value)
    } else {
      newArray = newArray.filter((item: string) => item !== value)
    }
    updateDemographic(field, newArray)
  }

  function renderFields() {
    if (!profile) return null

    // store all fields as value before using them, this approach allow re-ordering without large diff in git.
    let fields = {
      experience_level: (
        <div className="demographic-field">
          <label>
            <Locale
              en="Experience Level with 3D/VR"
              zh_cn="3D/VR经验水平"
              zh_hk="3D/VR經驗水平"
            />
          </label>
          <select
            value={profile.experience_level || ''}
            onChange={e =>
              updateDemographic('experience_level', e.target.value)
            }
          >
            <option value="">
              <Locale
                en="Select experience level"
                zh_cn="选择经验水平"
                zh_hk="選擇經驗水平"
              />
            </option>
            {experience_level_options.map(option => (
              <option key={option.value} value={option.value}>
                <Locale
                  en={option.en}
                  zh_cn={option.zh_cn}
                  zh_hk={option.zh_hk}
                />
              </option>
            ))}
          </select>
        </div>
      ),
      primary_use_case: (
        <div className="demographic-field">
          <label>
            <Locale en="Primary Use Case" zh_cn="主要用途" zh_hk="主要用途" />
          </label>
          <div className="checkbox-group">
            {[...primary_use_case_options, other_option].map(option => (
              <label key={option.value} className="checkbox-label">
                <input
                  type="checkbox"
                  checked={
                    (purpose.has_other && option.value === 'other') ||
                    purpose.predefined_values.includes(option.value)
                  }
                  onChange={event =>
                    updateDemographicArray(
                      'primary_use_case',
                      option.value,
                      event.target.checked,
                    )
                  }
                />
                <Locale
                  en={option.en}
                  zh_cn={option.zh_cn}
                  zh_hk={option.zh_hk}
                />
              </label>
            ))}
          </div>
          {purpose.has_other && (
            <input
              type="text"
              placeholder="Please specify your primary use case..."
              value={otherValue(purpose.other_values.join(', '))}
              onChange={e =>
                updateDemographic('primary_use_case', [
                  ...purpose.predefined_values,
                  ...e.target.value.trim().split(','),
                ])
              }
              className="other-input"
            />
          )}
        </div>
      ),

      preferred_content_types: (
        <div className="demographic-field">
          <label>
            <Locale
              en="Preferred Content Types"
              zh_cn="偏好的内容类型"
              zh_hk="偏好的內容類型"
            />
          </label>
          <div className="checkbox-group">
            {[...preferred_content_types_options, other_option].map(option => (
              <label key={option.value} className="checkbox-label">
                <input
                  type="checkbox"
                  checked={
                    (preferred_content_types.has_other &&
                      option.value === 'other') ||
                    preferred_content_types.predefined_values.includes(
                      option.value,
                    )
                  }
                  onChange={event =>
                    updateDemographicArray(
                      'preferred_content_types',
                      option.value,
                      event.target.checked,
                    )
                  }
                />
                <Locale
                  en={option.en}
                  zh_cn={option.zh_cn}
                  zh_hk={option.zh_hk}
                />
              </label>
            ))}
          </div>
          {preferred_content_types.has_other && (
            <input
              type="text"
              placeholder="Please specify your preferred content types..."
              value={otherValue(
                preferred_content_types.other_values.join(', '),
              )}
              onChange={e =>
                updateDemographic('preferred_content_types', [
                  ...preferred_content_types.predefined_values,
                  ...e.target.value.trim().split(','),
                ])
              }
              className="other-input"
            />
          )}
        </div>
      ),

      occupation: (
        <div className="demographic-field">
          <label>
            <Locale
              en="Occupation/Job Title"
              zh_cn="职业/职位"
              zh_hk="職業/職位"
            />
          </label>
          <select
            value={other_occupation ? 'other' : profile.occupation || ''}
            onChange={e => {
              if (e.target.value === 'other') {
                updateDemographic('occupation', 'other')
              } else {
                updateDemographic('occupation', e.target.value)
              }
            }}
          >
            <option value="">
              <Locale
                en="Select occupation"
                zh_cn="选择职业"
                zh_hk="選擇職業"
              />
            </option>
            {[...occupation_options, other_option].map(option => (
              <option key={option.value} value={option.value}>
                <Locale
                  en={option.en}
                  zh_cn={option.zh_cn}
                  zh_hk={option.zh_hk}
                />
              </option>
            ))}
          </select>
          {other_occupation && (
            <input
              type="text"
              placeholder="Please specify your occupation..."
              value={otherValue(profile.occupation)}
              onChange={e => updateDemographic('occupation', e.target.value)}
              className="other-input"
            />
          )}
        </div>
      ),

      industry: (
        <div className="demographic-field">
          <label>
            <Locale en="Industry/Sector" zh_cn="行业/领域" zh_hk="行業/領域" />
          </label>
          <select
            value={other_industry ? 'other' : profile.industry || ''}
            onChange={e => {
              if (e.target.value === 'other') {
                updateDemographic('industry', 'other')
              } else {
                updateDemographic('industry', e.target.value)
              }
            }}
          >
            <option value="">
              <Locale en="Select industry" zh_cn="选择行业" zh_hk="選擇行業" />
            </option>
            {[...industry_options, other_option].map(option => (
              <option key={option.value} value={option.value}>
                <Locale
                  en={option.en}
                  zh_cn={option.zh_cn}
                  zh_hk={option.zh_hk}
                />
              </option>
            ))}
          </select>
          {other_industry && (
            <input
              type="text"
              placeholder="Please specify your industry..."
              value={otherValue(profile.industry)}
              onChange={e => updateDemographic('industry', e.target.value)}
              className="other-input"
            />
          )}
        </div>
      ),

      company_size: (
        <div className="demographic-field">
          <label>
            <Locale en="Company Size" zh_cn="公司规模" zh_hk="公司規模" />
          </label>
          <select
            value={profile.company_size || ''}
            onChange={e => updateDemographic('company_size', e.target.value)}
          >
            <option value="">
              <Locale
                en="Select company size"
                zh_cn="选择公司规模"
                zh_hk="選擇公司規模"
              />
            </option>
            <option value="freelancer">
              <Locale
                en="Freelancer/Solo"
                zh_cn="自由职业者/个人"
                zh_hk="自由職業者/個人"
              />
            </option>
            <option value="2-10">
              <Locale
                en="2-10 employees"
                zh_cn="2-10名员工"
                zh_hk="2-10名員工"
              />
            </option>
            <option value="11-50">
              <Locale
                en="11-50 employees"
                zh_cn="11-50名员工"
                zh_hk="11-50名員工"
              />
            </option>
            <option value="51-200">
              <Locale
                en="51-200 employees"
                zh_cn="51-200名员工"
                zh_hk="51-200名員工"
              />
            </option>
            <option value="201-1000">
              <Locale
                en="201-1000 employees"
                zh_cn="201-1000名员工"
                zh_hk="201-1000名員工"
              />
            </option>
            <option value="1000+">
              <Locale
                en="1000+ employees"
                zh_cn="1000名以上员工"
                zh_hk="1000名以上員工"
              />
            </option>
            <option value="not-applicable">
              <Locale en="Not applicable" zh_cn="不适用" zh_hk="不適用" />
            </option>
          </select>
        </div>
      ),

      device_usage: (
        <div className="demographic-field">
          <label>
            <Locale
              en="Device/Platform Usage"
              zh_cn="设备/平台使用"
              zh_hk="設備/平台使用"
            />
          </label>
          <div className="checkbox-group">
            {[
              {
                value: 'mobile',
                en: 'Mobile',
                zh_cn: '移动设备',
                zh_hk: '移動設備',
              },
              {
                value: 'tablet',
                en: 'Tablet',
                zh_cn: '平板电脑',
                zh_hk: '平板電腦',
              },
              {
                value: 'desktop',
                en: 'Desktop/PC',
                zh_cn: '桌面/PC',
                zh_hk: '桌面/PC',
              },
              {
                value: 'vr',
                en: 'VR headset',
                zh_cn: 'VR头显',
                zh_hk: 'VR頭顯',
              },
              {
                value: 'ar',
                en: 'AR headset',
                zh_cn: 'AR头显',
                zh_hk: 'AR頭顯',
              },
            ].map(option => (
              <label key={option.value} className="checkbox-label">
                <input
                  type="checkbox"
                  checked={profile.device_usage?.includes(option.value)}
                  onChange={event =>
                    updateDemographicArray(
                      'device_usage',
                      option.value,
                      event.target.checked,
                    )
                  }
                />
                <Locale
                  en={option.en}
                  zh_cn={option.zh_cn}
                  zh_hk={option.zh_hk}
                />
              </label>
            ))}
          </div>
        </div>
      ),

      discovery_source: (
        <div className="demographic-field">
          <label>
            <Locale
              en="How did you discover our platform?"
              zh_cn="您是如何发现我们平台的？"
              zh_hk="您是如何發現我們平台的？"
            />
          </label>
          <select
            value={
              other_discovery_source ? 'other' : profile.discovery_source || ''
            }
            onChange={e => {
              if (e.target.value === 'other') {
                updateDemographic('discovery_source', 'other')
              } else {
                updateDemographic('discovery_source', e.target.value)
              }
            }}
          >
            <option value="">
              <Locale
                en="Select discovery source"
                zh_cn="选择发现来源"
                zh_hk="選擇發現來源"
              />
            </option>
            {[...discovery_source_options, other_option].map(option => (
              <option key={option.value} value={option.value}>
                <Locale
                  en={option.en}
                  zh_cn={option.zh_cn}
                  zh_hk={option.zh_hk}
                />
              </option>
            ))}
          </select>
          {other_discovery_source && (
            <input
              type="text"
              placeholder="Please specify how you discovered us..."
              value={otherValue(profile.discovery_source)}
              onChange={e =>
                updateDemographic('discovery_source', e.target.value)
              }
              className="other-input"
            />
          )}
        </div>
      ),

      region: (
        <div className="demographic-field">
          <label>
            <Locale en="Region" zh_cn="地区" zh_hk="地區" />
          </label>
          <input
            list="regionList"
            value={profile.region || ''}
            onChange={e => updateDemographic('region', e.target.value)}
            placeholder="Type or select your region..."
          />
          <datalist id="regionList">
            {regions?.map(region => {
              let names = Array.from(
                new Set([region.name, ...region.native_names]),
              )
              return (
                <option key={region.id}>
                  {region.flag} {names.join(' | ')}
                </option>
              )
            })}
          </datalist>
        </div>
      ),

      age_range: (
        <div className="demographic-field">
          <label>
            <Locale
              en="Age Range (Optional)"
              zh_cn="年龄范围（可选）"
              zh_hk="年齡範圍（可選）"
            />
          </label>
          <select
            value={profile.age_range || ''}
            onChange={e => updateDemographic('age_range', e.target.value)}
          >
            <option value="">
              <Locale
                en="Prefer not to say"
                zh_cn="不想透露"
                zh_hk="不想透露"
              />
            </option>
            <option value="under-18">
              <Locale en="Under 18" zh_cn="18岁以下" zh_hk="18歲以下" />
            </option>
            <option value="18-24">
              <Locale en="18-24" zh_cn="18-24岁" zh_hk="18-24歲" />
            </option>
            <option value="25-34">
              <Locale en="25-34" zh_cn="25-34岁" zh_hk="25-34歲" />
            </option>
            <option value="35-44">
              <Locale en="35-44" zh_cn="35-44岁" zh_hk="35-44歲" />
            </option>
            <option value="45-54">
              <Locale en="45-54" zh_cn="45-54岁" zh_hk="45-54歲" />
            </option>
            <option value="55-64">
              <Locale en="55-64" zh_cn="55-64岁" zh_hk="55-64歲" />
            </option>
            <option value="65+">
              <Locale en="65+" zh_cn="65岁以上" zh_hk="65歲以上" />
            </option>
          </select>
        </div>
      ),

      gender: (
        <div className="demographic-field">
          <label>
            <Locale
              en="Gender (Optional)"
              zh_cn="性别（可选）"
              zh_hk="性別（可選）"
            />
          </label>
          <select
            value={profile.gender || ''}
            onChange={e => updateDemographic('gender', e.target.value)}
          >
            <option value="">
              <Locale
                en="Prefer not to say"
                zh_cn="不想透露"
                zh_hk="不想透露"
              />
            </option>
            <option value="male">
              <Locale en="Male" zh_cn="男性" zh_hk="男性" />
            </option>
            <option value="female">
              <Locale en="Female" zh_cn="女性" zh_hk="女性" />
            </option>
            <option value="non-binary">
              <Locale en="Non-binary" zh_cn="非二元性别" zh_hk="非二元性別" />
            </option>
          </select>
        </div>
      ),
    }

    return (
      <>
        {/* Group 1: Usage & Experience */}

        {/* 1.1. Experience Level */}
        {fields.experience_level}

        {/* 1.2. Primary Use Case */}
        {fields.primary_use_case}

        {/* 1.3. Preferred Content Types */}
        {fields.preferred_content_types}

        {/* Group 2: Professional Context */}

        {/* 2.1. Occupation */}
        {fields.occupation}

        {/* 2.2. Industry */}
        {fields.industry}

        {/* 2.3. Company Size */}
        {fields.company_size}

        {/* Group 3: Technical & Discovery */}

        {/* 3.1. Device/Platform Usage */}
        {fields.device_usage}

        {/* 3.2. Discovery Source */}
        {fields.discovery_source}

        {/* Group 4: Personal Demographics */}

        {/* 4.1. Region */}
        {fields.region}

        {/* 4.2. Age Range (Optional) */}
        {fields.age_range}

        {/* 4.3. Gender (Optional) */}
        {fields.gender}
      </>
    )
  }

  const fields = renderFields()

  return (
    <>
      {!!saveDemographicError && (
        <div className="error">{saveDemographicError}</div>
      )}

      {fields}
    </>
  )
}
